import React, { useEffect } from 'react';
import { X, Heart, ShoppingCart, Trash2 } from 'lucide-react';
import { useWishlist } from '../context/WishlistContext';
import { useApp } from '../context/AppContext';
import { useCurrency } from '../context/CurrencyContext';

const WishlistModal = () => {
  const { fmt } = useCurrency();
  const { items, open, setOpen, remove, clear } = useWishlist();
  const { addToCart } = useApp();

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, setOpen]);

  if (!open) return null;

  return (
    <div
      data-testid="wishlist-modal"
      className="fixed inset-0 z-50 flex items-start justify-end bg-black/40 backdrop-blur-sm"
      onClick={() => setOpen(false)}
    >
      <div
        className="w-full sm:w-[440px] h-full bg-white dark:bg-[#0F0F10] shadow-2xl flex flex-col border-l border-neutral-200 dark:border-neutral-800"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200 dark:border-neutral-800">
          <div className="flex items-center gap-2">
            <Heart size={16} className="text-rose-500" fill="currentColor" />
            <h2 className="font-serif text-[18px] text-neutral-900 dark:text-neutral-100">Wishlist</h2>
            <span className="text-[12px] text-neutral-500">({items.length})</span>
          </div>
          <div className="flex items-center gap-2">
            {items.length > 0 && (
              <button
                data-testid="wishlist-clear"
                onClick={clear}
                className="text-[11.5px] text-neutral-500 hover:text-red-500"
              >Clear all</button>
            )}
            <button
              data-testid="wishlist-close"
              onClick={() => setOpen(false)}
              className="p-1.5 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-600 dark:text-neutral-300"
              aria-label="Close wishlist"
            ><X size={16} /></button>
          </div>
        </div>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center px-8 text-center text-neutral-500">
            <Heart size={38} className="mb-3 text-neutral-300 dark:text-neutral-700" />
            <p className="text-[14.5px] text-neutral-700 dark:text-neutral-300">Your wishlist is empty</p>
            <p className="text-[12.5px] mt-1">Tap the heart on any product to save it here.</p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto divide-y divide-neutral-100 dark:divide-neutral-800">
            {items.map((it) => (
              <div key={it.id} data-testid={`wishlist-item-${it.id}`} className="flex gap-3 p-4">
                <div className="w-16 h-16 rounded-md border border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center overflow-hidden shrink-0">
                  {it.image ? (
                    <img src={it.image} alt={it.name} className="max-h-full max-w-full object-cover" />
                  ) : (
                    <div className="text-[10px] text-neutral-400 text-center px-1">{it.catLabel}</div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] tracking-widest uppercase text-neutral-500 truncate">{it.catLabel}</div>
                  <div className="text-[13.5px] font-medium text-neutral-900 dark:text-neutral-100 line-clamp-2">{it.name}</div>
                  <div className="text-[11.5px] text-neutral-500 mt-0.5">{it.strength}{it.packaging ? ' · ' + it.packaging : ''}</div>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <div className="text-[14px] font-semibold text-neutral-900 dark:text-neutral-100">{fmt(it.price)}</div>
                    <div className="flex items-center gap-1.5">
                      <button
                        data-testid={`wishlist-add-cart-${it.id}`}
                        onClick={() => { addToCart(it); }}
                        className="inline-flex items-center gap-1 bg-[#1e293b] hover:bg-[#0f172a] text-white text-[11.5px] rounded-md px-2.5 py-1.5"
                      ><ShoppingCart size={11} /> Add</button>
                      <button
                        data-testid={`wishlist-remove-${it.id}`}
                        onClick={() => remove(it.id)}
                        className="p-1.5 rounded-md text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-red-500"
                        aria-label="Remove from wishlist"
                      ><Trash2 size={12} /></button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WishlistModal;
